import React from 'react';
import { API } from '../api';
import { sameNamePlayers, describePlayer } from '../utils/duplicateNames';
import Field from './ui/Field';

/*
 * Writing down a visitor.
 *
 * Lived inside the admin members tab until the Players page and the team
 * sheet wanted it too. The second of those is the one that mattered: a team
 * short a player on the day is exactly when a guest turns up, and before
 * this the admin had to leave the team, find the members tab, add the guest
 * there and come back to pick them from the list.
 *
 * `onAdded(created)` is told about the new player; what happens next — put
 * them in a team, open their profile — is the caller's business.
 */
function AddGuestForm({ clubId, onAdded, onCancel, submitLabel = 'Add guest player', users }) {
  const [name, setName] = React.useState('');
  const [email, setEmail] = React.useState('');
  const [error, setError] = React.useState('');
  const [saving, setSaving] = React.useState(false);

  /* Somebody of this name is already here.
   *
     Said inline rather than in a confirm box. The only thing being done is
     writing down a person, so the choice is simply whether a second record
     is wanted, and the answer wants the evidence on screen beside the box
     being typed into. */
  const nameClash = sameNamePlayers(users, name);
  const [clashAcknowledged, setClashAcknowledged] = React.useState(false);

  /* `guest: true` rather than a membership type, because the server sets both
     the membership type and the player type from it — a record carrying only
     one of them is a visitor who counts against the club's member limit. */
  const submit = async (e) => {
    e.preventDefault();
    if (nameClash.length && !clashAcknowledged) { setClashAcknowledged(true); return; }
    setSaving(true);
    setError('');
    try {
      const r = await fetch(`${API}/api/users`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          guest: true,
          name: name.trim(),
          email: email.trim() || null,
          ...(clubId ? { ClubId: clubId } : {}),
        }),
      });
      const created = await r.json().catch(() => ({}));
      if (!r.ok) { setError(created.error || 'That did not work.'); return; }
      setName('');
      setEmail('');
      setClashAcknowledged(false);
      await onAdded(created);
    } catch (err) {
      setError(err.message || 'That did not work.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <form className="ml-add-form" onSubmit={submit}>
      <p style={{ marginTop: 0, color: 'var(--gs-text-muted)', fontSize: 'var(--gs-text-sm)' }}>
        A guest plays but is not a member: no login, and they do not count towards
        the club’s member limit. You can put them in a team like anybody else.
      </p>
      <div className="gs-field-grid">
        <Field label="Name" htmlFor="guest-name">
          <input id="guest-name" className="w3-input" required autoFocus
            maxLength={120} value={name}
            /* Changing the name asks the question again. Without this,
               acknowledging one clash and then typing a different clashing
               name would go straight through. */
            onChange={e => { setName(e.target.value); setClashAcknowledged(false); }} />
        </Field>
        <Field label="Email" htmlFor="guest-email" help="Optional.">
          <input id="guest-email" className="w3-input" type="email"
            maxLength={160} value={email}
            onChange={e => setEmail(e.target.value)} />
        </Field>
      </div>
      {/* What there is to tell them apart, shown rather than promised. Asking
          "is this somebody new?" while displaying only the name — the thing
          that already matches — invites a yes that means nothing. */}
      {nameClash.length > 0 && (
        <div className="w3-panel w3-pale-yellow w3-leftbar w3-border-orange w3-round"
          style={{ padding: '10px 14px' }} role="status">
          <p style={{ margin: '0 0 8px', fontWeight: 600 }}>
            {nameClash.length === 1
              ? `There is already a “${name.trim()}” at this club.`
              : `There are already ${nameClash.length} people called “${name.trim()}” at this club.`}
          </p>
          <ul style={{ margin: '0 0 8px', paddingLeft: 20 }}>
            {nameClash.map(u => (
              <li key={u.id}>
                {describePlayer(u) || <em>nothing recorded to tell them apart</em>}
              </li>
            ))}
          </ul>
          <p style={{ margin: 0 }}>
            If this is the same person, they are already here and there is nothing
            to add. If it is somebody different, give them something that separates
            them on a draw sheet — a middle initial, or Jnr and Snr.
          </p>
        </div>
      )}
      {error && <div className="poster-error" role="alert">{error}</div>}
      <div style={{ display: 'flex', gap: 'var(--gs-space-2)', flexWrap: 'wrap' }}>
        <button type="submit" className="w3-button w3-round poster-btn-primary"
          disabled={!name.trim() || saving}>
          {saving
            ? 'Adding…'
            : (nameClash.length && clashAcknowledged ? 'Add anyway' : submitLabel)}
        </button>
        {onCancel && (
          <button type="button" className="w3-button w3-round w3-light-grey" onClick={onCancel}>
            Cancel
          </button>
        )}
      </div>
    </form>
  );
}

export default AddGuestForm;
